import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from '../GroupListing/GroupList.api';
import type { SubgroupFormData } from './SubgroupFormConfig';
import { defaultSubgroupFormValues, getFieldsToValidate, subgroupFormSchema } from './SubgroupFormConfig';
import type { FormMode } from './useSubgroupFormState';
import { useSubgroupFormState } from './useSubgroupFormState';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Modal from '@/components/common/Modal';
import InputField from '@/components/fields/InputField';
import TextareaField from '@/components/fields/TextareaField';

interface SubgroupFormProps {
    mode: FormMode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    subgroup?: SubgroupListItemDataFragment | null;
    group?: GroupListItemDataFragment | null;
    companyId: string;
    onSubmit: (data: SubgroupFormData) => Promise<void>;
    onClose: () => void;
}

const SubgroupFormContainer = ({
    mode,
    open,
    title,
    companyId,
    onSubmit,
    onClose,
    subgroup,
    group,
}: SubgroupFormProps) => {
    const { t } = useTranslation('groupDetails');
    const { t: tGlobal } = useTranslation('common');

    const coursesOptions = useMemo(() => {
        const coursesOptions = (group?.enrolledCourses || []).map(course => ({
            value: course.id,
            label: course.name,
        }));

        return coursesOptions;
    }, [group?.enrolledCourses]);

    const initialData = {
        name: subgroup?.name || '',
        description: subgroup?.description || '',
        parentGroupName: group?.name || '',
    };

    // Initialize form state
    const { state, actions } = useSubgroupFormState(mode, initialData);
    const form = useForm<SubgroupFormData>({
        mode: 'onChange',
        resolver: zodResolver(subgroupFormSchema),
        defaultValues: state.formData,
    });

    useEffect(() => {
        if (companyId && open) {
            const formData: SubgroupFormData = {
                name: initialData?.name || '',
                description: initialData?.description || '',
                parentGroupName: initialData?.parentGroupName || '',
            };
            actions.initialize(formData);
            form.reset(formData);
        }
    }, [companyId, open, form, actions, initialData?.name, initialData?.description, initialData?.parentGroupName]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                actions.reset();
                form.reset(defaultSubgroupFormValues);
            }, 150);
        }
    }, [open, actions, form]);

    // Memoized handleSubmit to avoid unnecessary re-renders in useMemo dependencies
    const handleSubmit = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }
        try {
            actions.submitStart();
            await onSubmit(form.getValues() as SubgroupFormData);
            actions.submitSuccess();
            onClose();
        } catch (error) {
            actions.submitError();
            if (error && typeof error === 'object' && 'fields' in error) {
                const fields = error.fields as { field: string; message: string }[];
                console.error('Validation errors:', fields);
                fields.forEach(err => {
                    if (err.field && err.message) {
                        form.setError(err.field as keyof SubgroupFormData, {
                            type: 'manual',
                            message: err.message,
                        });
                    }
                });
            }
        }
    }, [actions, onSubmit, form, onClose]);

    const modalFooter = (
        <div className="flex items-center justify-end gap-2.5">
            <Button outline onClick={onClose} disabled={state.isSubmitting} compact>
                {tGlobal('modal.cancel')}
            </Button>
            <Button color="teal" onClick={() => handleSubmit()} disabled={state.isSubmitting} compact>
                {mode === 'edit' ? t('modalEditSubgroup.updateButton') : t('modalCreateSubgroup.createButton')}
            </Button>
        </div>
    );

    const coursesList = coursesOptions.map(course => course.label).join(', ');

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={title}
                subtitle={mode === 'create' ? t('modalCreateSubgroup.subtitle', { groupName: group?.name }) : undefined}
                largeTitle
                footer={modalFooter}
                className="w-full !p-5 sm:!w-[512px] sm:!max-w-[512px]"
                showCloseButton={false}
            >
                {/* Form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    {/* Group Name and Subgroup Name in 2 columns */}
                    <div className="flex items-start justify-start gap-4">
                        <div className="flex flex-1 flex-col items-start justify-start">
                            <Input
                                label={t('fields.name.label')}
                                name=""
                                required={false}
                                disabled={true}
                                value={initialData?.parentGroupName}
                                labelSize="base"
                                labelWeight="semibold"
                            />
                        </div>
                        <div className="flex flex-1 flex-col items-start justify-start">
                            <InputField
                                label={t('fields.subgroupName.label')}
                                name="name"
                                placeholder={t('fields.subgroupName.placeholder')}
                                required
                                disabled={false}
                                labelSize="base"
                                labelWeight="semibold"
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <TextareaField
                        label={t('fields.subgroupDescription.label')}
                        name="description"
                        placeholder={t('fields.subgroupDescription.placeholder')}
                        rows={4}
                        labelSize="base"
                        labelWeight="semibold"
                    />

                    {/* Course - Read only */}
                    <div className="flex flex-col items-start justify-start gap-1.5">
                        <label className="block text-left leading-6 font-semibold text-neutral-800">
                            {t('fields.courses.label')}
                        </label>
                        <div className="flex w-full items-center justify-start gap-2.5 rounded-md bg-neutral-50 px-2.5 py-[7px] outline outline-1 outline-offset-[-1px] outline-neutral-300">
                            <div className="flex flex-1 items-center justify-start gap-2.5">
                                <div className="leading-6 font-medium text-neutral-500">
                                    {coursesList || t('fields.courses.empty')}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default SubgroupFormContainer;
